package top.wcpe.allincore.dsl.compiler.type

/**
 * DSL 编译期类型系统（D2-a 引入）。
 *
 * 用于：
 * - 局部 `var` 推断结果记录
 * - 后续 D2-b/c 阶段对 `await ... .payload.string(...)` 等后缀链做静态类型校验
 *
 * 设计要点：
 * - 仅区分语义足够粗的"运行期值类别"，不追求完整 Hindley-Milner
 * - 不可推断时统一退化为 [AnyT]，避免编译期假阳性
 * - [Nullable] 仅在显式 `null` 字面量或 `?.` 链等场景使用
 *
 * **M10 Stage 2 升级**：[ListT] / [MapT] 由 object 升级为携带泛型参数的 data class。
 * 缺省构造（`ListT()` / `MapT()`）等价于历史的"宽容器"语义（参数全为 [AnyT]）；
 * 携带具体泛型实参的实例（如 `ListT(NumberT)`）参与协变赋值判定。wire 协议（dsl-ir
 * IrType.java 的 ordinal）对泛型参数无感，仍按容器基座降级到 ordinal 3/4。
 */
sealed class IrType {

    /** 数值（IR 层 Double）。 */
    object NumberT : IrType()

    /** 字符串。 */
    object StringT : IrType()

    /** 布尔。 */
    object BooleanT : IrType()

    /**
     * 字典（Map<String, Any?>）。
     *
     * DSL 仅支持字符串 key（语法层保证）；[key] / [value] 均为 [AnyT] 时退化为"宽 Map"，
     * displayName 仍输出 "Map" 保持向后兼容。
     */
    data class MapT(val key: IrType = AnyT, val value: IrType = AnyT) : IrType()

    /**
     * 列表（List<Any?>）。
     *
     * [arg] 为 [AnyT] 时退化为"宽 List"，displayName 仍输出 "List" 保持向后兼容。
     */
    data class ListT(val arg: IrType = AnyT) : IrType()

    /** 未知 / 动态。所有类型互相相容。 */
    object AnyT : IrType()

    /** 无返回值（M5-c 引入；目前仅用于类型标注语义）。 */
    object UnitT : IrType()

    /** 可空类型包装。 */
    data class Nullable(val inner: IrType) : IrType()

    /**
     * data 类实例类型（M10 Stage 3 引入）。
     *
     * 用于跨文件类型流动场景：当 var/val 推断为某个 data 类的实例时，后续 MemberAccess 可
     * 通过 ProjectTypeTable.dataFields 反查字段类型。本类型仅在 IDE 跨文件分析路径产出，
     * runtime / wire 协议不感知（typeOrdinal 走 AnyT 路径降级；本类型不持久化到 schema）。
     */
    data class DataInstance(val className: String) : IrType()

    /**
     * 当前类型是否可赋值给 [target]。
     *
     * - 目标为 [AnyT]：放行
     * - 当前为 [AnyT]：放行（动态值，无法静态拒绝）
     * - 目标为 [Nullable]：同型或拆 inner 递归
     * - 当前为 [Nullable]：禁止赋给非空目标
     * - [ListT] / [MapT]：泛型协变——按各自参数递归 isAssignableTo
     *   （目标参数为 [AnyT] 时放行，对应"宽容器"目标）
     * - 其它：要求精确相等
     */
    fun isAssignableTo(target: IrType): Boolean {
        if (target == AnyT) return true
        if (this == AnyT) return true
        if (target is Nullable) return this == target || this.isAssignableTo(target.inner)
        if (this is Nullable) return false
        // ListT 协变：按元素类型递归。target.arg == AnyT 时等价于"宽 List"目标，自然放行。
        if (this is ListT && target is ListT) {
            return this.arg.isAssignableTo(target.arg)
        }
        // MapT 协变：key / value 各自递归。任一目标参数为 AnyT 时该侧放行。
        if (this is MapT && target is MapT) {
            return this.key.isAssignableTo(target.key) && this.value.isAssignableTo(target.value)
        }
        return this == target
    }

    /** 诊断展示用名称。 */
    fun displayName(): String = when (this) {
        NumberT -> "Number"
        StringT -> "String"
        BooleanT -> "Boolean"
        // 裸 MapT()（key/value 全 AnyT）保留历史展示 "Map"，避免破坏诊断文本兼容性。
        is MapT -> if (key == AnyT && value == AnyT) "Map" else "Map<${key.displayName()}, ${value.displayName()}>"
        // 裸 ListT()（arg 为 AnyT）保留历史展示 "List"。
        is ListT -> if (arg == AnyT) "List" else "List<${arg.displayName()}>"
        AnyT -> "Any"
        UnitT -> "Unit"
        is Nullable -> "${inner.displayName()}?"
        is DataInstance -> className
    }

    /**
     * 是否为"动态值"——AnyT 或 Nullable(AnyT)。
     *
     * 类型检查中常用作"放行守卫"：当 rhs 推断为动态值时，跳过严格的 isAssignableTo
     * 比较，避免对绝大多数旧 .allin 资源产生假阳性。
     */
    fun isDynamic(): Boolean = this == AnyT || (this is Nullable && this.inner == AnyT)
}
